import { Head, Link } from '@inertiajs/react';
import type { InvitesJudgesShow } from '@/types';
import { JudgePhotoCarousel } from '../components/JudgePhotoCarousel';
import { PastHacks } from '../components/PastHacks';
import { Ph26Shell } from '../components/Ph26Shell';

const JUDGING_STEPS = [
  {
    number: '01',
    title: 'Briefing',
    description:
      'Antes de comenzar te explicaremos la dinámica y los criterios de evaluación.',
  },
  {
    number: '02',
    title: 'Presentaciones',
    description:
      'Verás los proyectos que los equipos construyeron desde cero durante 36 horas.',
  },
  {
    number: '03',
    title: 'Evaluación',
    description:
      'Compartirás tu mirada con el resto del jurado para elegir a los ganadores.',
  },
] as const;

export default function Show({
  title,
  description,
  canonicalPath,
  siteUrl,
}: InvitesJudgesShow) {
  return (
    <Ph26Shell>
      <Head title={title}>
        <meta name="description" content={description} />
        <link rel="canonical" href={canonicalPath} />
      </Head>
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-8 md:py-12">
          <div className="mx-auto max-w-2xl space-y-10">
            <header className="text-center">
              <h1 className="inline-block font-bold font-sans text-2xl text-primary sm:text-3xl md:text-4xl">
                <span className="bg-primary px-3 py-2 text-background">
                  Invitación a ser juez
                </span>
              </h1>
              <p className="mt-4 text-primary text-xl sm:text-2xl md:text-3xl">
                <Link
                  href="/26-co"
                  className="lowercase transition-opacity hover:opacity-85"
                >
                  <span className="font-logo tracking-tight">
                    platanus hack [26]
                  </span>
                  <span className="font-title uppercase tracking-normal">
                    : bogotá
                  </span>
                </Link>
              </p>
              <p className="mt-2 font-mono text-primary/70 text-sm md:text-base">
                21-23 de agosto, 2026 • Bogotá, Colombia
              </p>
            </header>

            <section className="space-y-3 text-center">
              <p className="text-sm sm:text-base">
                Del 21 al 23 de agosto tendremos Platanus Hack 26: Bogotá ft.
                Buk. Tendremos a 100 ingenieros, devs y hackers crack
                construyendo un producto desde cero en 36 horas, con el auspicio
                de Anthropic, ElevenLabs, Vercel, Supabase, Render y más.{' '}
                <span className="bg-primary px-1 text-background">
                  Nos encantaría que fueras parte del jurado.
                </span>
              </p>
              <p className="text-muted-foreground text-sm sm:text-base">
                Como juez conocerás de primera mano lo que construyeron los
                equipos y sumarás tu experiencia para reconocer los proyectos
                más destacados de la hackatón.
              </p>
              <p className="text-muted-foreground text-sm sm:text-base">
                Además, compartirás con{' '}
                <strong className="text-primary">
                  100 de los hackers con mejor nivel de Bogotá
                </strong>
                : una oportunidad única para conocer talento y nuevas ideas.
              </p>
            </section>

            <section className="space-y-3">
              <h2 className="text-center font-bold text-sm uppercase tracking-wide">
                Así se vive el demo day
              </h2>
              <JudgePhotoCarousel />
            </section>

            <section className="space-y-4">
              <h2 className="text-center font-bold text-sm uppercase tracking-wide">
                Lo que necesitamos de ti
              </h2>
              <div className="border-2 border-primary bg-background/80 p-5">
                <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
                  <p className="font-bold uppercase tracking-wide">
                    Domingo 23 — Demo day
                  </p>
                  <p className="bg-primary px-2 py-0.5 font-mono font-semibold text-background text-xs uppercase tracking-wide">
                    09:30 – 15:30
                  </p>
                </div>
                <p className="mt-2 text-muted-foreground text-sm sm:text-base">
                  Te esperamos en la oficina para las presentaciones de los
                  equipos, la evaluación y la premiación. Antes de comenzar te
                  compartiremos la dinámica y los criterios de evaluación.
                </p>
              </div>
            </section>

            <section className="space-y-4">
              <h2 className="text-center font-bold text-sm uppercase tracking-wide">
                Cómo será la jornada
              </h2>
              <div className="grid gap-3 sm:grid-cols-3">
                {JUDGING_STEPS.map((step) => (
                  <div
                    key={step.number}
                    className="border border-primary/50 bg-background/50 p-4"
                  >
                    <p className="font-mono text-primary/60 text-xs">
                      {step.number}
                    </p>
                    <h3 className="mt-2 font-bold uppercase tracking-wide">
                      {step.title}
                    </h3>
                    <p className="mt-2 text-muted-foreground text-sm">
                      {step.description}
                    </p>
                  </div>
                ))}
              </div>
            </section>

            <div className="border-primary/30 border-t" />

            <PastHacks siteUrl={siteUrl} />

            <p className="text-center text-muted-foreground text-sm">
              ¿Te sumas como juez? Responde el mensaje que te llegó por WhatsApp
              y coordinamos todo.
            </p>
          </div>
        </div>
      </div>
    </Ph26Shell>
  );
}
